/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IMoeV2HazardBoxViewProps } from './moe-v2-hazard-box';
import { useState, useEffect } from 'react';

function useIsMobile(breakpoint = 768) {
    const getIsMobile = () => (typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false);

    const [isMobile, setIsMobile] = useState(getIsMobile);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);

    return isMobile;
}

const InfoIcon = () => (
    <svg className='moe-hazard-box__icon' width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
            d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
            stroke='currentColor'
            strokeWidth='2'
        />
        <path d='M12 16V12M12 8H12.01' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
    </svg>
);

const WarningIcon = () => (
    <svg className='moe-hazard-box__icon' width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
            d='M10.29 3.86L1.82 18C1.64537 18.3024 1.55296 18.6453 1.55199 18.9945C1.55102 19.3437 1.64151 19.6871 1.81445 19.9905C1.98738 20.2939 2.23675 20.5467 2.53773 20.7239C2.83871 20.901 3.18082 20.9962 3.53 21H20.47C20.8192 20.9962 21.1613 20.901 21.4623 20.7239C21.7633 20.5467 22.0126 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3437 22.448 18.9945C22.447 18.6453 22.3546 18.3024 22.18 18L13.71 3.86C13.5318 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3437 2.89725 12 2.89725C11.6563 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4682 3.56611 10.29 3.86Z'
            stroke='currentColor'
            strokeWidth='2'
        />
        <path d='M12 9V13M12 17H12.01' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
    </svg>
);

const ImportantIcon = () => (
    <svg className='moe-hazard-box__icon' width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
            d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
            stroke='currentColor'
            strokeWidth='2'
        />
        <path d='M12 8V12M12 16H12.01' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
    </svg>
);

export default (props: IMoeV2HazardBoxViewProps) => {
    const {
        hazardType,
        description,
        ctaText,
        ctaLink,
        fontSize,
        fontColor,
        borderColor,
        backgroundColor,
        paddingTop,
        paddingBottom,
        paddingLeftRight
    } = props.config;

    const getIcon = () => {
        switch (hazardType) {
            case 'warning':
                return <WarningIcon />;
            case 'important':
                return <ImportantIcon />;
            default:
                return <InfoIcon />;
        }
    };

    const getColor = () => {
        switch (hazardType) {
            case 'warning':
                return { backgroundColor: '#FDC13C', color: '#000' };
            case 'important':
                return { backgroundColor: '#D93B48', color: '#fff' };
            default:
                return { backgroundColor: '#28A0B1', color: '#fff' };
        }
    };

    const containerStyle = {
        border: borderColor ? `1px solid ${borderColor}` : undefined,
        backgroundColor: backgroundColor || getColor().backgroundColor,
        color: fontColor || getColor().color,
        fontSize: fontSize
    };

    const isMobile = useIsMobile();

    return (
        <div
            style={{
                width: '100%',
                paddingTop: isMobile ? '0rem' : paddingTop,
                paddingBottom: isMobile ? '0rem' : paddingBottom,
                paddingLeft: isMobile ? '0rem' : paddingLeftRight,
                paddingRight: isMobile ? '0rem' : paddingLeftRight
            }}
        >
            <div className={`moe-v2-module-container  moe-hazard-box`} style={containerStyle}>
                <div className='moe-hazard-box__content'>
                    {getIcon()}
                    <span className='moe-hazard-box__desc-cta'>
                        <span className='moe-hazard-box__description'>{description}</span>
                        {ctaText && ctaLink && (
                            <a
                                href={ctaLink.destinationUrl}
                                className='moe-hazard-box__cta'
                                role='button'
                                style={{ color: containerStyle.color, lineHeight: '1.4' }}
                            >
                                {ctaText}
                            </a>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};
